/**
 * @file `AdminHomePage` — the `/` landing for admin.
 * @module @labcraft/admin/ui/home/home-page
 *
 * Two shapes, picked by admin-session state:
 *
 *   - **Anonymous** (no admin session): renders a `SignInCard` in the
 *     page body — Google Workspace only.
 *   - **Signed in**: renders the cross-source {@link DashboardPanel} —
 *     the operator home (umbrella #883). `/` is the default landing and
 *     the `lc>` logo target.
 *
 * Reads the current admin identity via `useMe()` (TanStack-backed —
 * Phase 9 of umbrella #406). Anonymous is a wire value
 * (`{ admin: null }`), not a loading state; the loading branch
 * renders nothing so the chrome doesn't flicker between sign-in card
 * and dashboard while `/me` resolves.
 */

import { type JSX } from 'react'
import { SignInCard } from '@labcraft/ui'
import { useMe } from '../hooks/index.ts'
import { DashboardPanel } from '../dashboard/dashboard_panel.tsx'

/** Endpoint that starts the Workspace SSO flow on the admin daemon. */
const ADMIN_SIGNIN_BEGIN = '/api/account/google-workspace/authorize'

/** Top-level switch — anonymous vs signed-in. */
export function AdminHomePage(): JSX.Element | null {
  const me = useMe()

  if (!me.data) return null
  return me.data.admin === null ? <AnonymousHome /> : <DashboardPanel />
}

////////////////////////////////////////////////////////////////////////////////
// Anonymous
////////////////////////////////////////////////////////////////////////////////

/**
 * Centered sign-in card. Mirrors the popover content (Workspace-only
 * provider, same begin endpoint) but uses the larger card variant so
 * the in-page surface reads as the primary call to action.
 */
function AnonymousHome(): JSX.Element {
  return (
    <div className="home home--anonymous">
      <SignInCard
        variant="card"
        title="LabCraft Admin"
        lede="Operator console — sign in to continue."
        fineprint="// only @labcraft.dev domains permitted"
        onContinueWithGoogle={() => {
          window.location.assign(ADMIN_SIGNIN_BEGIN)
        }}
      />
    </div>
  )
}
